import { Component,  Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { Asset } from 'globals/store/assets.data';
import { Cue, JoiEvent, Ranking } from 'globals/store/events.data';
import { Program, Session } from 'globals/store/programs.data';
import { Wrapper } from '../../components/wrapper.component';
import { EntitySelectorData, EntitySelectorDialogComponent } from '../../components/entities/entity-selector.dialog.component';
import { openDialogGetResult } from '../../helpers/angular-helpers';
import { AddCueData, CueSheetViewComponent } from './cue-sheet-view.component';
import { assetListReselector, cueListReselector, cueMapReselector } from '../../store/reselectors/entity.reselector';
import { programPermissionsReselector } from '../../store/reselectors/programs.reselector';
import { SimpleSessionSelectorData, SimpleSessionSelectorDialogComponent } from '../../components/performers/session-selector.dialog.component';
import { NestedProgramInfo } from '../../store/reselectors/performerSponsors.reselector';
import { getXYFromEventAndWidth } from '../../helpers/helpers';
import { sessionMapReselector, sessionProgramListReselector } from '../../store/reselectors/sessions.reselector';
import { RankSelectDialogComponent, RankingDialogData } from '../sponsors/rank-select.dialog.component';
import { DragOption, DragDialogData, DragDialogComponent } from '../../components/wizards/drag.dialog.component';
import { getSet, jsonCopyObject } from 'globals/helpers/helpers';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'cue-sheet',
    template: `
<cue-sheet-view *ngIf="!!event?.cues && !!assets && !!session && !!program"
  [readOnly]="forceReadOnly || readOnly" 
  [assets]="assets" 
  [cues]="cueMap"
  [program]="program"
  [session]="session"
  [number]="number"
  (onSave)="saveSession()"
  (addCue)="addCue($event)"
  (copyInto)="copyInto($event)"
  (rearrangeCues)="rearrangeCues()"
></cue-sheet-view>
`,
    standalone: true,
    imports: [CueSheetViewComponent, CommonModule],
})
export class CueSheetComponent extends Wrapper<CueSheetComponent> implements OnInit, OnChanges {
  @Input() session: Session
  @Input() program: Program
  @Input() number: number

  @Input() forceReadOnly: boolean = false

  programs: Dictionary<Program>
  assets: Dictionary<Asset>
  sessionMap: Dictionary<Session> = {}
  event: JoiEvent
  fullAssetList: Asset[]
  fullCueList: Cue[]
  cueMap: Dictionary<Ranking>
  sessionProgramList: NestedProgramInfo[] = []
  readOnly: boolean

  constructor() {
    super()
    this.selectProp(assetListReselector, null, 'fullAssetList')
    this.selectProp(sessionMapReselector, null, 'sessionMap')
    this.selectProp(cueListReselector, null, 'fullCueList')
    this.selectProp(cueMapReselector, null, 'cueMap')
    this.subProp(this.store.select('assets', 'entities'), 'assets')
    this.subProp(this.store.select('events', 'event'), 'event')
    this.selectProp(sessionProgramListReselector, null, 'sessionProgramList')
    this.selectCallback<{is_readonly: boolean}, any>(programPermissionsReselector, null, ({ is_readonly }) => {
      this.readOnly = is_readonly
    })
  }

  ngOnInit() {

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.session) {
      this.session = jsonCopyObject(changes.session.currentValue)
    }
  }

  saveSession() {
    this.programService.updateSession(this.program.id, this.session)
  }

  
  async rearrangeCues() {

    const n = this.session.cues.rows.length
    if (n < 2) return

    const options: DragOption[] = Array.from({ length: n }, (_, x) => ({ id: x, title: `${x+1}` }))
    const originalOrder = JSON.stringify(options)

    const data: DragDialogData = {
      title: 'Cue Order',
      options,
      instructions: 'Drag to change the order of the cues in this session.',
      backgroundColor: '#54DDC7'
    }
    await openDialogGetResult(this.dialog, DragDialogComponent, { width: '180px', disableClose: true, data })

    if (JSON.stringify(data.options) === originalOrder) return

    const rows = data.options.map(o => this.session.cues.rows[o.id])
    this.session.cues.rows = rows
    this.saveSession()
  }
    

  async copyInto(event) {

    const data: SimpleSessionSelectorData = { ...getXYFromEventAndWidth(event, 300), program: this.program, sessionIds: [], singleSelection: true }
    const sessionId: string = await this.openDialogGetResult(SimpleSessionSelectorDialogComponent, { width: '300px', data, backdropClass: 'clear', disableClose: true })
    
    if (!sessionId) return
    const session = this.sessionMap[sessionId]

    session.cues.rows = [...session.cues.rows, ...jsonCopyObject(this.session.cues.rows)]
    this.programService.updateSession(this.program.id, session)
    
  }



  async addCue({ key, index, type, event }: AddCueData) {

    const cueName = this.program.cueCols.find(c => c.id === key).name
    const usedIds = this.session.cues.rows[index].cues[key]?.cueIds || []
    console.log({ key, index, type, usedIds, cueName })

    if (type === 'cue') {
      const { y: top, x: left } = getXYFromEventAndWidth(event, 300)
      const position = { top, left }
      

      const groupRanks = this.event.cues.filter(c => c.id.includes(key))
      const usedRanks = groupRanks.filter(c => !!usedIds.includes(c.id))
      const shownRanks = groupRanks.filter(c => !usedIds.includes(c.id))

      const data: RankingDialogData = {
        position,
        cueName,
        cueKey: key,
        ranks: shownRanks,
        selectedRankId: null,
        type: 'Cue'
      }

      const oldRanksSerialized = JSON.stringify([...data.ranks, ...usedRanks ])

      const { selectedRankId, ranks } = await openDialogGetResult<{ selectedRankId: string, ranks: Ranking[] }>(this.dialog, RankSelectDialogComponent, { width: '300px', disableClose: false, data, backdropClass: 'clear' }) // mutates data in place
      const allGroupRanks = [ ...ranks, ...usedRanks ]

      if (JSON.stringify(allGroupRanks) !== oldRanksSerialized) {
        console.log("Updating cue templates?")
        const cueGroupIds = getSet(ranks, 'id')
        
        this.event.cues.filter(c => !cueGroupIds.has(c.id) && !c.id.includes(key)).forEach(c => allGroupRanks.push(c)) // add back in cues filtered out above before saving
        this.eventService.updateCues(allGroupRanks)
      }
      if (!!selectedRankId) {

        if (!this.session.cues.rows[index].cues[key]) {
          this.session.cues.rows[index].cues[key] = { cueIds: [] }
        }
        const cue = this.session.cues.rows[index].cues[key]
        cue.cueIds.push(selectedRankId)
        this.saveSession()
      }
      return

    } else {
      const data: EntitySelectorData = { 
        entities: this.fullAssetList, 
        entityName: 'Asset',
        searchPlaceholder: `Search or add asset`, 
        rect: (<HTMLElement>event.target).getBoundingClientRect(), 
        entityHolderName: cueName,
        limitSelectionToOne: true,
        canEditNames: true
      }
      openDialogGetResult(this.dialog, EntitySelectorDialogComponent, { width: '300px', data, disableClose: true, backdropClass: 'clear' }).then((selectedIds: string[]) => {
        if (!selectedIds?.length) return
        const selected = selectedIds[0]
  
        if (!this.session.cues.rows[index].cues[key]) this.session.cues.rows[index].cues[key] = {}
        const cue = this.session.cues.rows[index].cues[key]
  
        cue.assetId = selected  
        this.saveSession()
      })
    }
    
  }




}

